import React, { useContext, useState } from 'react';
import {
  getMaxLength,
  ModalCustomStyle,
  memberIdReg,
  FormTitles,
  FormTitleTranslationKeys,
  NavigateConst,
  domainForSegment,
  getErrorTypeByCode,
  SCREEN_NAMES,
  ANALYTICS_MESSAGES,
  getAnalyticsBaseConfig,
  getPlanType,
} from 'common';
import { useTranslation } from 'react-i18next';
import Modal from 'react-modal';
import MemberIdOverlay from './MemberIdOverlay';
import { Buffer } from 'buffer';
import { useRegisterMember } from '@sentara-health/sentara-api-hooks-core';
import { useLocation, useNavigate } from 'react-router-dom';
import MemberInfoModal from './MemberInfoModal';
import {
  CloseIconBlock,
  ErrorMessage,
  Label,
  CallSupport,
  Button,
  InputField,
  GlobalError,
  SelectDropdown,
  Headings,
  Loader,
  Link,
  HelpIcon,
  Paragraph,
} from '@sentara-health/optimahealth_web';
import { useForm } from 'react-hook-form';
import { GlobalContext } from '../../context';
import { useAdobeAnalytics } from '@sentara-health/platform-analytics';
import { AdobeFormConfig } from 'common/AdobeFormConfig';

interface GetStartedProps {
  setPage: (response: any) => void;
  setPlantypes: (response: any) => void;
  setFormData: (response: any) => void;
  page: number;
  formData: any;
  item: any;
}

type FormInputs = {
  regMemId: string;
  planType: string;
};

function GetStarted({
  setPage,
  setPlantypes,
  setFormData,
  page,
  formData,
  item,
}: Readonly<GetStartedProps>) {
  const { t } = useTranslation(); /** translation function*/
  const { RegisterMember } = useRegisterMember(); /** API function  for register member*/
  const [errorMessage, setErrorMessage] =
    useState<boolean>(false); /** Setting default error message*/
  const [noContent, setNoContent] =
    useState<boolean>(false); /** default error meesage for 204 no content*/
  const [loader, setLoader] =
    useState<boolean>(false); /** Default loader value set to false*/
  const [isOpen, setIsOpen] =
    useState<boolean>(false); /** Default open value set to false*/
  const [memberErrorModal, setMemberErrorModal] = useState<boolean>(false);
  const [memberErrorMessage, setMemberErrorMessage] = useState<string>('');
  const { getFeature, logging } = useContext(GlobalContext);
  const { formSubmissionSucceeded, formSubmissionFailed } = useAdobeAnalytics();
  const { pathname } = useLocation();
  const {
    handleSubmit,
    register,
    watch,
    setValue,
    getValues,
    reset,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<FormInputs>({
    defaultValues: {
      regMemId: formData[page]?.regMemId ?? '',
      planType: formData[page]?.planType ?? '',
    },
  });
  const navigate = useNavigate();
  const I_DONT_KNOW_API_VALUE = "I don’t know"; // Right single quotation mark (char code 8217) from API
  const ACTIVATION_LABEL_MEDICAREID = t('common.getmedicareId');
  const ACTIVATION_LABEL_MEDICAID = t('common.getmedicaId');
  const ACTIVATION_LABEL_INDIVIUALEMPLOYER = t('common.individualEmployer');

  const onSubmit = async () => {
    const { regMemId, planType } = getValues();
    if (planType !== '' && regMemId === '') {
      register('regMemId', { required: false });
    }
    if (regMemId !== '' && planType === '') {
      register('planType', { required: false });
    }
    if (regMemId === '' && planType === '') {
      setError('planType', { type: 'required' });
      setError('regMemId', { type: 'required' });
    }
    let plan = {
      regMemId: regMemId,
      planType: planType,
    };
    if (regMemId) {
      const memberId = {
        memberId: Buffer.from(regMemId).toString('base64'),
      };
      const response = await RegisterMember(memberId);
      handleApiResponse(response, planType);
    } else if (planType && planType !== I_DONT_KNOW_API_VALUE) {
      if (planType === ACTIVATION_LABEL_INDIVIUALEMPLOYER) {
        setPlantypes(getPlanType.individual);
      } else if (planType === ACTIVATION_LABEL_MEDICAID) {
        setPlantypes(getPlanType.medicaid);
      } else if (planType === ACTIVATION_LABEL_MEDICAREID) {
        setPlantypes(getPlanType.medicare);
      }
      setPage((currPage: number) => currPage + 1);
    }
    const path = `${domainForSegment}${pathname}`
    formSubmissionSucceeded({
      segment: path,
      form: AdobeFormConfig['getStartedSuccess']
    });
    setFormData({ ...formData, [page]: plan });
  };

  const handleApiResponse = (response: any, planType: string) => {
    if (response?.errorCode === 403 && response?.errorDetails?.detail) {
      setLoader(false);
      setMemberErrorModal(true);
      setMemberErrorMessage(response?.errorDetails?.detail);
      // Fire error event for API error
      formSubmissionFailed({
        segment: `${domainForSegment}${pathname}`,
        form: {
          ...AdobeFormConfig['getStartedError'],
          formFailReason: 'API Error: ' + response?.errorDetails?.detail
        }
      });
      logging?.error?.({
        message: ANALYTICS_MESSAGES.REG_PLAN_EXCEPTION,
        screenName: SCREEN_NAMES.REGISTRATION,
        additionalDetails: {
          ...getAnalyticsBaseConfig({ userId: '' }),
          step: page,
          planType: planType,
        },
        apiDetails: {
          exception: {
            errorType: getErrorTypeByCode(response?.errorCode?.toString()),
            message: response?.errorDetails?.detail,
            stack: response?.error,
          },
          response: { httpStatus: response?.errorCode },
        },
      });
      return;
    } else if (response?.data?.isActivated === true) {
      setLoader(false);
      setErrorMessage(true);
      // Fire error event for already activated member
      formSubmissionFailed({
        segment: `${domainForSegment}${pathname}`,
        form: {
          ...AdobeFormConfig['getStartedError'],
          formFailReason: 'Member already activated'
        }
      });
      logging?.error?.({
        message: ANALYTICS_MESSAGES.REG_PLAN_EXCEPTION,
        screenName: SCREEN_NAMES.REGISTRATION,
        additionalDetails: {
          ...getAnalyticsBaseConfig({ userId: '' }),
          step: page,
          planType: planType,
          errorType: getErrorTypeByCode('isActivated'),
        },
        apiDetails: {
          exception: {
            errorType: getErrorTypeByCode('isActivated'),
            message: '',
            stack: '',
          },
          response: { httpStatus: '' },
        },
      });
      return;
    } else if (!response?.data || response?.errorCode) {
      setNoContent(true);
      setLoader(false);
      // Fire error event for no content/service error
      formSubmissionFailed({
        segment: `${domainForSegment}${pathname}`,
        form: {
          ...AdobeFormConfig['getStartedError'],
          formFailReason: 'Service error: ' + (response?.errorCode || 'No data received')
        }
      });
      logging?.error?.({
        message: ANALYTICS_MESSAGES.REG_PLAN_EXCEPTION,
        screenName: SCREEN_NAMES.REGISTRATION,
        additionalDetails: {
          ...getAnalyticsBaseConfig({ userId: '' }),
          step: page,
          planType: planType,
        },
        apiDetails: {
          exception: {
            errorType: getErrorTypeByCode(response?.errorCode ?? ''),
            message: response?.errorCode,
            stack: response?.error,
          },
          response: { httpStatus: response?.errorCode },
        },
      });
      return;
    } else if (response?.data?.individualMember === true) {
      setPlantypes(t('activation.individual'));
    } else if (response?.data?.medicareMember === true) {
      setPlantypes(ACTIVATION_LABEL_MEDICAREID);
    } else if (response?.data?.medicaidMember === true) {
      setPlantypes(ACTIVATION_LABEL_MEDICAID);
    }
    setPage(1);
  };

  function openModal() {
    setIsOpen(true);
  }
  function closeModal() {
    setIsOpen(false);
  }
  const closeMemberErrorModal = () => {
    setMemberErrorModal(false);
    setMemberErrorMessage('');
  };

  const childrenData = (
    <>
      <option selected disabled={true} value="">
        {t('activation.selectPlanType')}
      </option>
      {item?.data
        ?.filter((value: { planName: string }) =>
          getFeature?.MedicarePlan ?? value?.planName?.toLowerCase() !== 'medicare'
        )
        ?.map((value: { planName: string }) => (
          <option
            key={value.planName}
            data-testid="select-option"
            value={value.planName}
          >
            {value.planName === I_DONT_KNOW_API_VALUE ? t('activation.idontknow') : value.planName}
          </option>
        ))}
    </>
  );

  const renderErrorMessage = () => {
    if (errorMessage) {
      return (
        <GlobalError data-testid="memberExistId" variant="mb-3">
          <Paragraph
            text={
              <>
                {t('activation.getStartedMemberAlreadyRegistered1')}
                <Link to={NavigateConst.signIn} className="px-1">{t('activation.sign')}</Link>
                {t('activation.getStartedMemberAlreadyRegistered2')}
                <Link to={NavigateConst.forgotUsername} className="px-1">
                  {t('common.userName')}
                </Link>
                {t('activation.getStartedMemberAlreadyRegistered3')}
              </>
            }
            variant="regular"
            dataTestId="memberExistIdText"
            className="mb-0"
          />
        </GlobalError>
      );
    }
    if (noContent) {
      return (
        <GlobalError variant="mb-3" dataTestId="forgotUsernameMemIdError">
          {t('common.serviceErrorRequestId')}
        </GlobalError>
      );
    }
    return null;
  };

  const renderPlanTypeErrorMessage = () => {
    if (errors.planType) {
      return <ErrorMessage>{t('activation.planType')}</ErrorMessage>;
    }
    return null;
  };

  return (
    <>
      <form
        className="get_started"
        onSubmit={handleSubmit(onSubmit)}
        autoComplete="off"
      >
        {loader && <Loader />}
        <div className="row">
          <div className="col-lg-6 col-md-8 col-12 mt-n2" aria-live="polite">
            {renderErrorMessage()}
            {FormTitles[page] !== '' && (
              <>
                <span className="visually-hidden me-1">
                  {t('common.registrationCurrentPage')}{' '}
                  {[page + 1]}
                </span>
                <Headings
                  level={1}
                  text={t(FormTitleTranslationKeys[FormTitles[page]])}
                  variant="semiBold"
                  dataTestId="registrationHeading"
                  className="mb-4 pb-4"
                />
              </>
            )}
            {getFeature?.MemberIdRegistrationField ? (
              <>
                <div className="mt-1 mb-2 position-relative">
                  <Label
                    className="pb-1"
                    dataTestId="memberIdLabel"
                    htmlFor="regMemId"
                  >
                    {t('common.memberId')}
                  </Label>
                  <div className="align-items-center">
                    <div className="position-relative flex-1 d-flex">
                      <InputField
                        id="regMemId"
                        data-testid="memberId"
                        maxLength={getMaxLength.memberId}
                        placeholder={t('common.enterMemberID')}
                        className={`${errors.regMemId ? 'error-border' : ''}`}
                        {...register('regMemId', {
                          onChange: (
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            setValue(
                              'regMemId',
                              e.target.value.replace(memberIdReg, '')
                            );
                            reset({ planType: '' });
                            e.target.value === ''
                              ? setError('regMemId', { type: 'required' })
                              : clearErrors('regMemId');
                          },
                        })}
                      />
                      <div className="ms-2">
                        <Button
                          data-testid="memIdHelpInfo"
                          onClick={openModal}
                          aria-label="Information icon click to view"
                          variant="icon_btn"
                          type={'button'}
                        >
                          <HelpIcon />
                        </Button>
                      </div>
                    </div>
                    {errors.regMemId && (
                      <div className="mt-1">
                        <ErrorMessage>
                          {t('common.memberIdRequired')}
                        </ErrorMessage>
                      </div>
                    )}
                  </div>
                </div>
                <div
                  className="or-text pt-2"
                  data-testid="getStartedOrText"
                >
                  {t('activation.or')}
                </div>
              </>
            ) : null}
            <div className="mb-3 mt-1 position-relative">
              <Label
                className="pb-1"
                dataTestId="planTypeLabel"
                htmlFor="regPlanType"
              >
                {t('activation.plantype')}
              </Label>
              <SelectDropdown
                dataTestId="planTypeSelect"
                className={`form-select ${errors.planType ? 'error-border' : ''}`}
                id="planType"
                aria-label={t('activation.plantype')}
                aria-labelledby="planTypeLabel"
                title={t('activation.plantype')}
                {...register('planType', {
                  onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                    e.preventDefault();
                    reset({ regMemId: '' });
                    setValue('planType', e.target.value);
                    e.target.value === ''
                      ? setError('planType', { type: 'required' })
                      : clearErrors('planType');
                  },
                })}
              >
                {childrenData}
              </SelectDropdown>
              {watch('planType') === I_DONT_KNOW_API_VALUE && (
                <div className="pt-4">
                  <div className="mb-3">
                    <CallSupport dataTestId="memServiceNum">
                      {t('common.callMember1')}
                    </CallSupport>
                  </div>
                  <div className="mb-3">
                    <CallSupport dataTestId="mainNum">
                      {t('common.callMember2')}
                    </CallSupport>
                  </div>
                  <div className="mb-3">
                    <CallSupport dataTestId="hearingImpairmentNum">
                      {t('common.callMember3')}
                    </CallSupport>
                  </div>
                </div>
              )}
              <div aria-live="polite" className="mt-1">
                {renderPlanTypeErrorMessage()}
              </div>
            </div>
          </div>
        </div>
        <div className="row pt-4 pb-2">
          {watch('planType') === I_DONT_KNOW_API_VALUE ? null : (
            <div className="col-md-auto col-12 mb-4">
              <Button
                dataTestId={'continueButton'}
                variant="primary_btn"
                className="btn-md-width"
                disabled={!(getValues('regMemId') || getValues('planType'))}
                type="submit"
              >
                {t('common.continue')}
              </Button>
            </div>
          )}
          <div className="col-md-auto col-12 mb-4">
            <Button
              type="button"
              dataTestId={'cancelButton'}
              variant="secondary_btn"
              className="btn-md-width"
              onClick={(e: React.MouseEvent<HTMLElement>) => {
                e.preventDefault();
                formSubmissionFailed({
                  segment: `${domainForSegment}${pathname}`,
                  form: AdobeFormConfig['getStartedCancel']
                });
                logging?.trace?.({
                  message: ANALYTICS_MESSAGES.REG_PLAN_CANCEL,
                  screenName: SCREEN_NAMES.REGISTRATION,
                  additionalDetails: {
                    ...getAnalyticsBaseConfig({ userId: '' }),
                    step: page,
                    planType: getValues('planType'),
                    reason: ANALYTICS_MESSAGES.USER_CLICKED_CANCEL,
                  },
                });
                navigate(NavigateConst.signIn);
              }}
            >
              {t('common.cancel')}
            </Button>
          </div>
        </div>
      </form>

      <Modal
        isOpen={isOpen}
        onRequestClose={closeModal}
        data-testid="memberIdModal"
        contentLabel="Member Id Informal Overlay"
        style={ModalCustomStyle}
      >
        <div className="w-member-overlay">
          <div className="d-flex justify-content-end mb-2">
            <Button
              type="button"
              data-testid="btn-close"
              iconLeft={<CloseIconBlock />}
              variant="icon_btn"
              onClick={closeModal}
              aria-label="Close"
            >
              {t('common.close')}
            </Button>
          </div>
          <MemberIdOverlay />
        </div>
      </Modal>
      {memberErrorMessage && (
        <MemberInfoModal
          closeMemberErrorModal={closeMemberErrorModal}
          memberErrorMessage={memberErrorMessage}
          memberErrorModal={memberErrorModal}
        />
      )}
    </>
  );
};

export default GetStarted;